/**
 * CouponWelcomePopup — "Have a Coupon or Referral Code?" unified modal.
 *
 * ONE input field, TWO independent reward engines. Automatic server-side
 * detection via POST /api/codes/apply. No prefill. No private codes shown.
 *
 * Appears exactly once per eligible onboarding journey:
 *   - User is authenticated
 *   - Backend reports they still have a redeemable welcome coupon OR we
 *     show it anyway so a user with ONLY a referral code can enter it.
 *   - User has NOT yet dismissed it this journey (localStorage per user).
 */
import React, { useEffect, useState } from "react";
import { Gift, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/hooks/useAuth";

const DISMISS_KEY = (uid) => `kalki_coupon_welcome_dismissed_${uid}`;

export default function CouponWelcomePopup() {
  const { user, loading } = useAuth();
  const [visible, setVisible] = useState(false);
  const [code, setCode] = useState("");           // ← ALWAYS empty default
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);   // { type, credits?, balance?, title, message }

  // Allow any caller (e.g. Profile Dropdown → Redeem Code) to force-open
  // this modal regardless of localStorage dismissal state.
  useEffect(() => {
    const open = () => {
      setSuccess(null);
      setCode("");
      setVisible(true);
    };
    window.addEventListener("kalki:open-redeem-code", open);
    return () => window.removeEventListener("kalki:open-redeem-code", open);
  }, []);

  useEffect(() => {
    if (loading || !user) return;
    const uid = user.user_id || user.id || user.email;
    if (!uid) return;
    if (localStorage.getItem(DISMISS_KEY(uid))) return;
    let alive = true;
    (async () => {
      try {
        // Show the popup if the user still has a redeemable coupon OR
        // if they have a referral code to potentially attach. The
        // simplest trigger today is coupon eligibility — a user who
        // already redeemed can still enter a referral via the Wallet
        // sheet, which is sufficient.
        const { data } = await api.get("/coupons/eligibility");
        if (!alive) return;
        if (data?.already_redeemed === false && data?.active) {
          setVisible(true);
        } else {
          localStorage.setItem(DISMISS_KEY(uid), "1");
        }
      } catch (e) {
        // Silent — this is non-blocking UI.
      }
    })();
    return () => { alive = false; };
  }, [user, loading]);

  if (!visible) return null;

  const uid = user?.user_id || user?.id || user?.email;

  const dismiss = () => {
    if (uid) localStorage.setItem(DISMISS_KEY(uid), "1");
    setVisible(false);
    window.dispatchEvent(new CustomEvent("kalki:coupon-skipped"));
  };

  const apply = async () => {
    const normalized = (code || "").trim().toUpperCase();
    if (!normalized) return;
    setBusy(true);
    try {
      const { data } = await api.post("/codes/apply", { code: normalized });
      if (data?.type === "coupon") {
        setSuccess({
          type: "coupon",
          credits: data.credits,
          balance: data.balance,
          title: data.title || "Coupon Applied!",
          message: data.message || "Your eligible KALKI Credits have been added to your wallet.",
        });
        if (typeof data.balance === "number") {
          window.dispatchEvent(new CustomEvent("kalki:credits-updated",
            { detail: { balance: data.balance } }));
        }
      } else if (data?.type === "referral") {
        setSuccess({
          type: "referral",
          title: data.title || "Referral Code Applied!",
          message: data.message || "Your referral has been recorded. Eligible rewards will be credited after the required steps are completed.",
        });
      } else {
        setSuccess({ type: "generic", title: "Code Applied", message: "Code recorded." });
      }
      setTimeout(() => {
        if (uid) localStorage.setItem(DISMISS_KEY(uid), "1");
        setVisible(false);
      }, 2600);
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      // Specific toast titles for the user-visible error states listed in spec.
      const err = d.code;
      if (err === "ALREADY_REDEEMED" || err === "ALREADY_ATTRIBUTED") {
        toast.error(d.message || "This code has already been used on your account.", {
          description: "Code Already Redeemed",
        });
      } else if (err === "EXPIRED" || err === "INACTIVE" || err === "CAP_REACHED") {
        toast.error(d.message || "This code is no longer available.");
      } else {
        toast.error(d.message || "Invalid Code. Please check your code and try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="coupon-welcome-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coupon-welcome-title"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(10, 7, 30, 0.72)",
        backdropFilter: "blur(10px)",
        padding: "16px",
      }}
    >
      <div
        style={{
          width: "min(420px, 100%)",
          background: "linear-gradient(180deg, #1A0B3F 0%, #0E0527 100%)",
          color: "#F6F1FF",
          borderRadius: "22px",
          border: "1px solid rgba(179, 132, 255, 0.22)",
          boxShadow: "0 32px 80px rgba(55, 0, 170, 0.55)",
          padding: "28px 22px 22px",
          textAlign: "center",
          position: "relative",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <button
          data-testid="coupon-welcome-close"
          onClick={dismiss}
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 32, height: 32, borderRadius: 999,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "#D7CBFF", cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <X size={16} />
        </button>

        {!success ? (
          <>
            <div style={{
              width: 72, height: 72, borderRadius: 999,
              margin: "4px auto 14px",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: "radial-gradient(circle at 30% 30%, #B384FF 0%, #5D00FF 60%, #2B0075 100%)",
              boxShadow: "0 8px 32px rgba(93, 0, 255, 0.55)",
            }}>
              <Gift size={32} color="#FFF" strokeWidth={1.6} />
            </div>
            <h3
              id="coupon-welcome-title"
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em",
                margin: "0 0 6px",
              }}
            >
              Have a Coupon or Referral Code?
            </h3>
            <p style={{ fontSize: 14, lineHeight: 1.5, color: "#CFC2FA", margin: "0 0 20px" }}>
              Enter it below to unlock your reward.
            </p>
            <input
              data-testid="coupon-welcome-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Enter Coupon or Referral Code"
              aria-label="Coupon or Referral Code"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              style={{
                width: "100%", padding: "14px 14px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(179, 132, 255, 0.32)",
                color: "#F6F1FF",
                fontSize: 15, letterSpacing: "0.05em",
                textAlign: "center", textTransform: "uppercase",
                outline: "none",
                marginBottom: 14,
              }}
            />
            <button
              data-testid="coupon-welcome-redeem"
              onClick={apply}
              disabled={busy || !code?.trim()}
              style={{
                width: "100%", padding: "14px 16px",
                borderRadius: 12,
                background: busy ? "rgba(93,0,255,0.5)" : "linear-gradient(135deg, #7E3BFF 0%, #B384FF 100%)",
                color: "#FFF", fontWeight: 600, letterSpacing: "0.04em",
                fontSize: 14, cursor: busy ? "wait" : "pointer",
                border: "none",
                marginBottom: 10,
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {busy ? "Applying…" : "Apply Code"}
            </button>
            <button
              data-testid="coupon-welcome-skip"
              onClick={dismiss}
              style={{
                width: "100%", padding: "12px 16px",
                borderRadius: 12,
                background: "transparent",
                color: "#CFC2FA", fontSize: 13, letterSpacing: "0.04em",
                border: "1px solid rgba(179, 132, 255, 0.22)",
                cursor: "pointer",
              }}
            >
              Continue Without Code
            </button>
          </>
        ) : (
          <div data-testid="coupon-welcome-success" style={{ padding: "6px 0 4px" }}>
            <div style={{
              width: 72, height: 72, borderRadius: 999,
              margin: "4px auto 14px",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(135deg, #15B881 0%, #0E8966 100%)",
              boxShadow: "0 8px 32px rgba(21, 184, 129, 0.5)",
            }}>
              <Check size={34} color="#FFF" strokeWidth={2} />
            </div>
            <h3 style={{
              fontFamily: "'Fraunces', serif",
              fontSize: 22, fontWeight: 500,
              margin: "0 0 6px",
            }} data-testid={`coupon-welcome-success-${success.type}`}>
              {success.type === "coupon" && typeof success.credits === "number"
                ? `${success.credits} KALKI Credits Added!`
                : (success.title || "Code Applied!")}
            </h3>
            {success.type === "coupon" ? (
              <p style={{ fontSize: 13, color: "#CFC2FA", margin: 0 }}>
                New balance: <strong style={{ color: "#F6F1FF" }}>{success.balance}</strong> credits
              </p>
            ) : (
              <p style={{ fontSize: 13, color: "#CFC2FA", margin: 0, lineHeight: 1.5 }}>
                {success.message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
